import { useState, useEffect } from 'react'
import { FiSave, FiUser, FiMail, FiPhone, FiBriefcase, FiMapPin, FiPackage } from 'react-icons/fi'
import { useLanguageStore } from '../../store/languageStore'
import { useAuthStore } from '../../store/authStore'
import { apiClient } from '../../api/client'
import { API_ENDPOINTS } from '../../api/endpoints'
import toast from 'react-hot-toast'
import SideSlider from './SideSlider'

interface ProfileSliderProps {
  isOpen: boolean
  onClose: () => void
}

export default function ProfileSlider({ isOpen, onClose }: ProfileSliderProps) {
  const { language } = useLanguageStore()
  const { user, mergeUser } = useAuthStore()
  const isRTL = language === 'ar'

  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [mobileNumber, setMobileNumber] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    if (!isOpen) return

    const load = async () => {
      setIsLoading(true)
      try {
        const response = await apiClient.get(API_ENDPOINTS.auth.me)
        const me = response.data || {}
        setFirstName(me.firstName || '')
        setLastName(me.lastName || '')
        setEmail(me.email || '')
        setPhoneNumber(me.phoneNumber || '')
        setMobileNumber(me.mobileNumber || '')
        mergeUser({
          firstName: me.firstName,
          lastName: me.lastName,
          email: me.email,
          phoneNumber: me.phoneNumber,
          mobileNumber: me.mobileNumber,
          companyName: me.companyName,
          companyCode: me.companyCode,
          branchCode: me.branchCode,
          assignedStoreId: me.assignedStoreId,
          assignedStoreName: me.assignedStoreName,
          role: me.role,
          roles: me.roles,
        })
      } catch {
        setFirstName(user?.firstName || '')
        setLastName(user?.lastName || '')
        setEmail(user?.email || '')
        setPhoneNumber(user?.phoneNumber || '')
        setMobileNumber(user?.mobileNumber || '')
      } finally {
        setIsLoading(false)
      }
    }

    void load()
  }, [isOpen])

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) onClose()
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isOpen, onClose])

  useEffect(() => {
    if (isOpen) document.body.style.overflow = 'hidden'
    else document.body.style.overflow = ''
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSaving(true)
    try {
      const response = await apiClient.put(API_ENDPOINTS.auth.updateProfile, {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        phoneNumber: phoneNumber.trim(),
        mobileNumber: mobileNumber.trim(),
      })
      const data = response.data || {}
      mergeUser({
        firstName: data.firstName ?? firstName.trim(),
        lastName: data.lastName ?? lastName.trim(),
        email: data.email ?? email.trim(),
        phoneNumber: data.phoneNumber ?? phoneNumber.trim(),
        mobileNumber: data.mobileNumber ?? mobileNumber.trim(),
      })
      toast.success(isRTL ? 'تم تحديث الملف الشخصي بنجاح' : 'Profile updated successfully')
      onClose()
    } catch (error: any) {
      toast.error(
        error?.response?.data?.messageAr ||
          error?.response?.data?.message ||
          (isRTL ? 'فشل تحديث الملف الشخصي' : 'Failed to update profile')
      )
    } finally {
      setIsSaving(false)
    }
  }

  if (!isOpen) return null

  const displayName =
    [firstName, lastName].filter(Boolean).join(' ') || user?.username || 'User'

  return (
    <SideSlider
      isOpen={isOpen}
      onClose={onClose}
      isRTL={isRTL}
      title={isRTL ? 'الملف الشخصي' : 'Profile'}
      widthClass="max-w-md"
      contentClassName="p-6"
    >
          {isLoading ? (
            <p className="text-center text-gray-500 py-10">{isRTL ? 'جاري التحميل...' : 'Loading...'}</p>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="flex flex-col items-center gap-2 pb-4 border-b border-gray-100">
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary-500 to-purple-600 flex items-center justify-center text-white text-2xl font-bold">
                  {(displayName[0] || 'U').toUpperCase()}
                </div>
                <p className="font-semibold text-gray-800">{displayName}</p>
                <p className="text-sm text-gray-500">{user?.username}</p>
              </div>

              <div className="grid grid-cols-1 gap-4">
                <Field label={isRTL ? 'الاسم الأول' : 'First name'} value={firstName} onChange={setFirstName} icon={<FiUser />} />
                <Field label={isRTL ? 'اسم العائلة' : 'Last name'} value={lastName} onChange={setLastName} icon={<FiUser />} />
                <Field label={isRTL ? 'البريد الإلكتروني' : 'Email'} value={email} onChange={setEmail} icon={<FiMail />} type="email" />
                <Field label={isRTL ? 'الهاتف' : 'Phone'} value={phoneNumber} onChange={setPhoneNumber} icon={<FiPhone />} />
                <Field label={isRTL ? 'الجوال' : 'Mobile'} value={mobileNumber} onChange={setMobileNumber} icon={<FiPhone />} />
              </div>

              <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3 text-sm">
                <p className="font-medium text-gray-700">{isRTL ? 'معلومات الحساب' : 'Account info'}</p>
                <InfoRow icon={<FiBriefcase />} label={isRTL ? 'الشركة' : 'Company'} value={user?.companyName || user?.companyCode || '-'} />
                <InfoRow icon={<FiMapPin />} label={isRTL ? 'الفرع' : 'Branch'} value={user?.branchCode || '-'} />
                <InfoRow icon={<FiPackage />} label={isRTL ? 'المخزن' : 'Warehouse'} value={user?.assignedStoreName || user?.assignedStoreId || '-'} />
                <InfoRow icon={<FiUser />} label={isRTL ? 'الدور' : 'Role'} value={user?.role || '-'} />
              </div>

              <button
                type="submit"
                disabled={isSaving}
                className="w-full flex items-center justify-center gap-2 bg-primary-600 hover:bg-primary-700 text-white py-3 rounded-lg font-medium disabled:opacity-50"
              >
                <FiSave />
                {isSaving ? (isRTL ? 'جاري الحفظ...' : 'Saving...') : isRTL ? 'حفظ التغييرات' : 'Save changes'}
              </button>
            </form>
          )}
    </SideSlider>
  )
}

function Field({
  label,
  value,
  onChange,
  icon,
  type = 'text',
}: {
  label: string
  value: string
  onChange: (v: string) => void
  icon: React.ReactNode
  type?: string
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1.5">{label}</label>
      <div className="relative">
        <span className="absolute top-1/2 -translate-y-1/2 text-gray-400 start-3">{icon}</span>
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full ps-10 pe-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
      </div>
    </div>
  )
}

function InfoRow({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="flex items-center gap-2 text-gray-600">
      <span className="text-gray-400">{icon}</span>
      <span className="min-w-[4.5rem] text-gray-500">{label}:</span>
      <span className="font-medium text-gray-800 truncate">{value}</span>
    </div>
  )
}
